import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { paymentLinkingApi } from "../../paymentLinking/services/paymentLinkingApi";

// Helper untuk error handling
const handleApiError = (error) => {
  console.error('[PaymentLinkingSlice] Error:', error);
  
  // Extract error details dari response API
  if (error.response?.data) {
    const apiError = error.response.data;
    return {
      message: apiError.message || apiError.response_text || 'An error occurred',
      code: apiError.response_code || '01',
      status: error.response.status,
      errors: apiError.errors
    };
  }
  
  return {
    message: error.message || 'Network error occurred',
    code: '01',
    status: 500
  };
};

// Async thunks
export const initializePaymentSession = createAsyncThunk(
  "paymentLinking/initializeSession",
  async ({ linkCode, fcmToken = null, deviceInfo = null }, { rejectWithValue, dispatch }) => {
    try {
      console.log('[Redux] Initializing session for:', linkCode);
      
      // Clear any existing session first
      paymentLinkingApi.clearSession();
      
      const response = await paymentLinkingApi.initializeSession(linkCode, fcmToken, deviceInfo);
      
      // Fetch details immediately after session init
      await dispatch(fetchPaymentLinkDetails(linkCode));
      
      return {
        sessionId: response.data.session_id,
        linkCode,
        expiresAt: response.data.expires_at,
        fcmRegistered: response.data.fcm_registered || false,
        isInitialized: true
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const fetchPaymentLinkDetails = createAsyncThunk(
  "paymentLinking/fetchDetails",
  async (linkCode, { rejectWithValue }) => {
    try {
      console.log('[Redux] Fetching details for:', linkCode);
      const response = await paymentLinkingApi.getPaymentLinkDetails(linkCode);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const processPaymentLinking = createAsyncThunk(
  "paymentLinking/processPayment",
  async ({ linkCode, paymentData, idempotencyKey = null }, { rejectWithValue }) => {
    try {
      const response = await paymentLinkingApi.processPayment(linkCode, paymentData, idempotencyKey);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const checkPaymentLinkingStatus = createAsyncThunk(
  "paymentLinking/checkStatus",
  async ({ linkCode, transactionId = null }, { rejectWithValue }) => {
    try {
      const response = await paymentLinkingApi.checkPaymentStatus(linkCode, transactionId);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const fetchSavedMethods = createAsyncThunk(
  "paymentLinking/getSavedMethods",
  async (linkCode, { rejectWithValue }) => {
    try {
      const response = await paymentLinkingApi.getSavedMethods(linkCode);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const refreshPaymentSession = createAsyncThunk(
  "paymentLinking/refreshSession",
  async (linkCode, { rejectWithValue }) => {
    try {
      const response = await paymentLinkingApi.refreshSession(linkCode);
      return {
        expiresAt: response.data.expires_at,
        extendedBy: response.data.extended_by
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const initialState = {
  // Session state (tidak dipersist)
  session: {
    sessionId: null,
    linkCode: null,
    expiresAt: null,
    fcmRegistered: false,
    isInitialized: false,
    isValid: false
  },

  // Payment link details (dipersist)
  paymentLink: null,

  // Payment processing
  transaction: null,
  paymentStatus: null,

  // Saved methods (dipersist)
  savedMethods: [],
  allowNewMethod: false,

  // Loading states
  loading: {
    initialize: false,
    details: false,
    process: false,
    status: false,
    savedMethods: false
  },

  // Error states
  error: null,
  errorCode: null,
  validationErrors: null,

  // Cache
  lastUpdated: null
};

const paymentLinkingSlice = createSlice({
  name: "paymentLinking",
  initialState,
  reducers: {
    clearPaymentLinkingError: (state) => {
      state.error = null;
      state.errorCode = null;
      state.validationErrors = null;
    },

    clearTransaction: (state) => {
      state.transaction = null;
      state.paymentStatus = null;
    },

    resetPaymentLinking: (state) => {
      // Hapus session state saja, biarkan data yang dipersist
      return {
        ...state,
        ...initialState,
        paymentLink: state.paymentLink, // Keep payment link
        savedMethods: state.savedMethods // Keep saved methods
      };
    },

    setSessionValid: (state, action) => {
      state.session.isValid = action.payload;
    },

    clearSession: (state) => {
      state.session = initialState.session;
      paymentLinkingApi.clearSession();
    },

    updatePaymentStatus: (state, action) => {
      if (state.transaction?.transaction_id === action.payload.transaction_id) {
        state.paymentStatus = action.payload;
      }
    }
  },

  extraReducers: (builder) => {
    builder
      // Initialize Session
      .addCase(initializePaymentSession.pending, (state) => {
        state.loading.initialize = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(initializePaymentSession.fulfilled, (state, action) => {
        state.loading.initialize = false;
        state.session = {
          ...action.payload,
          isValid: true
        };
        state.error = null;
      })
      .addCase(initializePaymentSession.rejected, (state, action) => {
        state.loading.initialize = false;
        state.session.isValid = false;
        state.error = action.payload?.message;
        state.errorCode = action.payload?.code;
        state.validationErrors = action.payload?.errors;
      })

      // Fetch Payment Link Details
      .addCase(fetchPaymentLinkDetails.pending, (state) => {
        state.loading.details = true;
        state.error = null;
      })
      .addCase(fetchPaymentLinkDetails.fulfilled, (state, action) => {
        state.loading.details = false;
        state.paymentLink = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPaymentLinkDetails.rejected, (state, action) => {
        state.loading.details = false;
        state.error = action.payload?.message;
        state.errorCode = action.payload?.code;
        // Clear session jika unauthorized
        if (action.payload?.status === 401) {
          state.session.isValid = false;
        }
      })

      // Process Payment
      .addCase(processPaymentLinking.pending, (state) => {
        state.loading.process = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(processPaymentLinking.fulfilled, (state, action) => {
        state.loading.process = false;
        state.transaction = action.payload;
        state.error = null;
      })
      .addCase(processPaymentLinking.rejected, (state, action) => {
        state.loading.process = false;
        state.error = action.payload?.message;
        state.errorCode = action.payload?.code;
        state.validationErrors = action.payload?.errors;
        // Clear session jika unauthorized
        if (action.payload?.status === 401) {
          state.session.isValid = false;
        }
      })

      // Check Payment Status
      .addCase(checkPaymentLinkingStatus.pending, (state) => {
        state.loading.status = true;
      })
      .addCase(checkPaymentLinkingStatus.fulfilled, (state, action) => {
        state.loading.status = false;
        state.paymentStatus = action.payload;
      })
      .addCase(checkPaymentLinkingStatus.rejected, (state, action) => {
        state.loading.status = false;
        state.error = action.payload?.message;
        // Clear session jika unauthorized
        if (action.payload?.status === 401) {
          state.session.isValid = false;
        }
      })

      // Fetch Saved Methods
      .addCase(fetchSavedMethods.pending, (state) => {
        state.loading.savedMethods = true;
      })
      .addCase(fetchSavedMethods.fulfilled, (state, action) => {
        state.loading.savedMethods = false;
        state.savedMethods = action.payload.saved_methods || [];
        state.allowNewMethod = action.payload.allow_new_method;
      })
      .addCase(fetchSavedMethods.rejected, (state, action) => {
        state.loading.savedMethods = false;
        state.error = action.payload?.message;
      })

      // Refresh Session
      .addCase(refreshPaymentSession.fulfilled, (state, action) => {
        state.session.expiresAt = action.payload.expiresAt;
        state.session.isValid = true;
      })
      .addCase(refreshPaymentSession.rejected, (state, action) => {
        state.session.isValid = false;
        state.error = action.payload?.message;
      });
  }
});

// Selectors
export const selectIsLoading = (state) => 
  Object.values(state.paymentLinking.loading).some(loading => loading);

export const selectSessionValid = (state) => 
  state.paymentLinking.session.isValid;

export const selectPaymentLink = (state) => 
  state.paymentLinking.paymentLink;

export const selectSavedMethods = (state) => 
  state.paymentLinking.savedMethods;

export const selectTransaction = (state) => 
  state.paymentLinking.transaction;

export const selectPaymentStatus = (state) => 
  state.paymentLinking.paymentStatus;

export const {
  clearPaymentLinkingError,
  clearTransaction,
  resetPaymentLinking,
  setSessionValid,
  clearSession,
  updatePaymentStatus
} = paymentLinkingSlice.actions;

export default paymentLinkingSlice.reducer;